import { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";

import {
  Accordion,
  Button,
  Checkbox,
  Icon,
  Label,
  Loader,
  Menu,
  Modal,
  Tab,
  TabPane,
} from "semantic-ui-react";
import styled from "styled-components";

import { roleInfo } from "simplydo/core/permissions";
import type { OpenAPI } from "simplydo/interfaces";
import { Input } from "simplydo/react/component-library";

import api from "api";
import util from "utils/utils";

import RoleTable from "components/lib/Roles/RoleTable";

import RoleEditorAssignment from "./Assignment";
import RoleEditorPermissions, { PermissionTable } from "./Permissions";
import RoleEditorSettings from "./Settings";

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: ${({ theme }) => (theme.sizes.isMobile ? "column" : "row")};
  margin-bottom: 10px;
  gap: 10px;
`;

export type Permission = {
  key: string; // OpenAPI.Schemas["Role"]["permissions"][number];
  title: string;
  text: string;
  dependsOn?: {
    key: string; // OpenAPI.Schemas["Role"]["permissions"][number];
    reason?: string;
    type?: "recommended";
  }[];
};

export type PermissionCategory = { name: string; permissions: Permission[] };

export type PermissionGroup = {
  id: string;
  type: keyof typeof roleInfo;
  title: string;
  permissions: PermissionCategory[];
};

type RoleEditorProps = {
  forType: OpenAPI.Schemas["Role"]["forType"];
  permissionGroups: PermissionGroup[];
  forId: string;
  canCreateNewRoles?: boolean;
  canSetRolesAsDefault?: boolean;
};

const RoleEditor = ({
  forType,
  forId,
  permissionGroups,
  canCreateNewRoles,
  canSetRolesAsDefault,
}: RoleEditorProps) => {
  // Updating roles state
  const [roles, setRoles] = useState<OpenAPI.Schemas["Role"][]>([]);
  const [
    managingPermissionsForRoleModalId,
    setManagingPermissionsForRoleModalId,
  ] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [deletingRole, setDeletingRole] = useState<string>("");

  const [roleNameSearch, setRoleNameSearch] = useState("");
  const [rolePermissionSearch, setRolePermissionSearch] = useState("");

  const filteredRoles = useMemo(() => {
    const nameSearch = roleNameSearch.toLowerCase();
    const permissionSearch = rolePermissionSearch.toLowerCase();

    if (!nameSearch && !permissionSearch) {
      return roles;
    }

    return roles.filter((role) => {
      const nameMatch = role.name.toLowerCase().includes(nameSearch);
      if (nameSearch && !nameMatch) {
        return false;
      }
      if (!permissionSearch) {
        return true;
      }
      for (const permission of role.permissions) {
        if (permission.toLowerCase().includes(permissionSearch)) {
          return true;
        }
      }

      return false;
    });
  }, [roleNameSearch, rolePermissionSearch, roles]);

  // Creating new role state
  const [roleCreationModalOpen, setRoleCreationModalOpen] =
    useState<boolean>(false);
  const [creatingRole, setCreatingRole] = useState<boolean>(false);
  const [newRoleName, setNewRoleName] = useState<string>("");
  const [newRolePermissions, setNewRolePermissions] = useState<
    OpenAPI.Schemas["Role"]["permissions"]
  >([]);

  const usingForId = forType === "global" ? "global" : forId;
  const managingRoleModalRole = useMemo(
    () => roles.find((role) => role._id === managingPermissionsForRoleModalId),
    [managingPermissionsForRoleModalId, roles],
  );
  const [permissionsTabActive, setPermissionsTabActive] = useState(false);
  const [activePermissionGroup, setActivePermissionGroup] = useState<
    string | null
  >(permissionGroups[0]?.id ?? null);

  const addPermissionsToRole = useCallback(
    (roleId: string, permissions: string[]) => {
      api.roles.addPermissionsToRole(
        forType,
        usingForId,
        roleId,
        permissions,
        ({ role: updatedRole }) => {
          setRoles((prevRoles) =>
            prevRoles.map((role) =>
              role._id === updatedRole._id ? { ...role, ...updatedRole } : role,
            ),
          );
        },
        (err) => {
          toast.error(err.message);
        },
      );
    },
    [forType, usingForId],
  );

  const removePermissionsFromRole = useCallback(
    (roleId: string, permissions: string[]) => {
      api.roles.removePermissionsFromRole(
        forType,
        usingForId,
        roleId,
        permissions,
        ({ role: updatedRole }) => {
          setRoles((prevRoles) =>
            prevRoles.map((role) =>
              role._id === updatedRole._id ? { ...role, ...updatedRole } : role,
            ),
          );
        },
        (err) => {
          toast.error(err.message);
        },
      );
    },
    [forType, usingForId],
  );

  const getRoles = useCallback(() => {
    setLoading(true);
    api.roles.get(
      forType,
      usingForId,
      {},
      (data) => {
        setRoles(data.roles);
        setLoading(false);
      },
      (err) => {
        toast.error(err.message);
        setLoading(false);
      },
    );
  }, [usingForId, forType]);

  const createRole = useCallback(() => {
    setCreatingRole(true);
    api.roles.createRole(
      forType,
      usingForId,
      { name: newRoleName, permissions: newRolePermissions },
      ({ role: newRole }) => {
        setCreatingRole(false);
        setRoles((prevRoles) => [newRole, ...prevRoles]);
        setNewRoleName("");
        setNewRolePermissions([]);
        setRoleCreationModalOpen(false);
      },
      (err) => {
        toast.error(err.message);
        setCreatingRole(false);
      },
    );
  }, [forType, newRoleName, newRolePermissions, usingForId]);

  const deleteRole = useCallback(
    (roleId: string) => {
      util
        .confirm(
          "Delete role",
          `Are you sure you want to delete the role "${roles.find((role) => role._id === roleId).name}"? This is permanent and cannot be undone. All users assigned to this role will have the role revoked and the role will be deleted.`,
        )
        .then(() => {
          setDeletingRole(roleId);
          api.roles.deleteRole(
            forType,
            usingForId,
            roleId,
            () => {
              setManagingPermissionsForRoleModalId("");
              setDeletingRole("");
              setRoles((prevRoles) =>
                prevRoles.filter((role) => role._id !== roleId),
              );
            },
            (err) => {
              toast.error(err.message);
              setDeletingRole("");
            },
          );
        })
        .catch(() => {});
    },
    [forType, roles, usingForId],
  );

  useEffect(() => {
    getRoles();
  }, [getRoles]);

  const handleSelectAll = () => {
    if (roleCreationModalOpen) {
      setNewRolePermissions(
        permissionGroups.flatMap((group) =>
          group.permissions.flatMap((category) =>
            category.permissions.map((p) => p.key),
          ),
        ),
      );
    } else {
      util
        .confirm(
          `Adding all permissions to ${managingRoleModalRole.name}`,
          "Are you sure you want to add all permissions to this role? You can remove them at any time.",
        )
        .then(() => {
          addPermissionsToRole(
            managingRoleModalRole._id,
            permissionGroups.flatMap((group) =>
              group.permissions.flatMap((category) =>
                category.permissions.map((p) => p.key),
              ),
            ),
          );
        })
        .catch(() => {});
    }
  };

  const handleDeselectAll = () => {
    if (roleCreationModalOpen) {
      setNewRolePermissions([]);
    } else {
      util
        .confirm(
          `Removing all permissions from ${managingRoleModalRole.name}`,
          "Are you sure you want to remove all permissions from this role? You can add them back at any time.",
        )
        .then(() => {
          removePermissionsFromRole(
            managingRoleModalRole._id,
            managingRoleModalRole?.permissions,
          );
        })
        .catch(() => {});
    }
  };

  return (
    <div>
      <Modal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={roleCreationModalOpen}
        onClose={() => setRoleCreationModalOpen(false)}
      >
        <Modal.Header>Create new role</Modal.Header>
        <Modal.Content
          scrolling
          style={{
            maxHeight: "80vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Tab
            menu={{ secondary: true, pointing: true }}
            style={{ flex: 1, display: "flex", flexDirection: "column" }}
            panes={[
              {
                menuItem: "Settings",
                render: () => (
                  <div>
                    <Input
                      placeholder="Role name"
                      value={newRoleName}
                      onChange={(e) => setNewRoleName(e.target.value)}
                      fluid
                    />
                  </div>
                ),
              },
              {
                menuItem: "Permissions",
                render: () => (
                  <Accordion styled fluid>
                    {permissionGroups.map((group) => (
                      <>
                        <Accordion.Title
                          key={group.id}
                          onClick={() =>
                            setActivePermissionGroup(
                              activePermissionGroup === group.id ?
                                null
                              : group.id,
                            )
                          }
                          active={activePermissionGroup === group.id}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <div style={{ display: "flex", gap: 4 }}>
                              <Icon name={roleInfo[group.type].icon} />
                              {group.title}
                            </div>
                            <Label size="tiny">
                              {
                                newRolePermissions.filter((p) =>
                                  group.permissions.some((category) =>
                                    category.permissions.some(
                                      (perm) => perm.key === p,
                                    ),
                                  ),
                                ).length
                              }
                              /
                              {group.permissions.reduce(
                                (acc, category) =>
                                  acc + category.permissions.length,
                                0,
                              )}{" "}
                              assigned
                            </Label>
                          </div>
                        </Accordion.Title>
                        <Accordion.Content
                          key={group.id + "-content"}
                          active={activePermissionGroup === group.id}
                        >
                          <PermissionTable
                            groupId={group.id}
                            permissionCategories={group.permissions}
                            usingRole={{
                              _id: "new",
                              name: newRoleName,
                              permissions: newRolePermissions,
                            }}
                            toggleIndividualPermission={(permission) => {
                              setNewRolePermissions((prevPermissions) =>
                                prevPermissions.includes(permission) ?
                                  prevPermissions.filter(
                                    (p) => p !== permission,
                                  )
                                : [...prevPermissions, permission],
                              );
                            }}
                          />
                        </Accordion.Content>
                      </>
                    ))}
                  </Accordion>
                ),
              },
            ]}
          />
        </Modal.Content>

        <Modal.Actions>
          <Checkbox
            style={{ marginRight: 10 }}
            label={
              newRolePermissions.length > 0 ? "Deselect All" : "Select All"
            }
            toggle
            checked={newRolePermissions.length > 0}
            onChange={() => {
              if (newRolePermissions.length > 0) {
                handleDeselectAll();
              } else {
                handleSelectAll();
              }
            }}
          />
          <Button
            content="Cancel"
            onClick={() => setRoleCreationModalOpen(false)}
          />
          <Button
            content="Create role"
            icon="plus"
            primary
            onClick={createRole}
            loading={creatingRole}
            disabled={!newRoleName}
          />
        </Modal.Actions>
      </Modal>

      <Modal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={!!managingPermissionsForRoleModalId}
        onClose={() => setManagingPermissionsForRoleModalId("")}
      >
        <Modal.Header>
          Managing role "{managingRoleModalRole?.name}"{" "}
          {managingRoleModalRole?.icon?.name ?
            <Icon
              name={managingRoleModalRole.icon.name}
              color={managingRoleModalRole.icon.colour}
            />
          : null}
        </Modal.Header>
        <Modal.Content
          scrolling
          style={{
            minHeight: "80vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Tab
            onTabChange={(e, data) => {
              const tabTitle = data.panes[data.activeIndex]?.menuItem;
              setPermissionsTabActive(tabTitle === "Permissions");
            }}
            menu={{ secondary: true, pointing: true }}
            style={{ flex: 1, display: "flex", flexDirection: "column" }}
            panes={[
              {
                menuItem: "Settings",
                render: () => (
                  <RoleEditorSettings
                    canSetRolesAsDefault={canSetRolesAsDefault}
                    forType={forType}
                    forId={usingForId}
                    usingRole={managingRoleModalRole}
                    setRoles={setRoles}
                    roleId={managingPermissionsForRoleModalId}
                    roles={roles}
                  />
                ),
              },
              {
                menuItem: "Permissions",
                render: () => (
                  <RoleEditorPermissions
                    removePermissionsFromRole={removePermissionsFromRole}
                    addPermissionsToRole={addPermissionsToRole}
                    forType={forType}
                    forId={usingForId}
                    permissionGroups={permissionGroups}
                    usingRole={managingRoleModalRole}
                    setRoles={setRoles}
                  />
                ),
              },
              managingRoleModalRole?.isDefaultRole ? null : (
                {
                  menuItem: "Assignment",
                  render: () => (
                    <RoleEditorAssignment
                      forType={forType}
                      forId={usingForId}
                      usingRole={managingRoleModalRole}
                      setRoles={setRoles}
                    />
                  ),
                }
              ),
            ]}
          />
        </Modal.Content>
        <Modal.Actions>
          {permissionsTabActive &&
            !managingRoleModalRole?.preventUserEditing && (
              <Checkbox
                label={
                  managingRoleModalRole?.permissions.length > 0 ?
                    "Deselect All"
                  : "Select All"
                }
                toggle
                checked={managingRoleModalRole?.permissions.length > 0}
                onChange={() => {
                  if (managingRoleModalRole?.permissions.length > 0) {
                    handleDeselectAll();
                  } else {
                    handleSelectAll();
                  }
                }}
              />
            )}
          {!managingRoleModalRole?.preventUserDeletion ?
            <Button
              content="Delete role"
              icon="trash"
              basic
              onClick={() => deleteRole(managingPermissionsForRoleModalId)}
              loading={deletingRole === managingPermissionsForRoleModalId}
            />
          : null}

          <Button
            content="Done"
            onClick={() => {
              setManagingPermissionsForRoleModalId("");
              setPermissionsTabActive(false);
            }}
          />
        </Modal.Actions>
      </Modal>

      <FilterContainer>
        {canCreateNewRoles ?
          <Button
            content="Create new role"
            icon="plus"
            primary
            fluid
            style={{ maxWidth: 200 }}
            onClick={() => {
              setActivePermissionGroup(permissionGroups[0]?.id ?? null);
              setRoleCreationModalOpen(true);
            }}
          />
        : null}

        <Input
          icon="search"
          placeholder="Search by name..."
          value={roleNameSearch}
          onChange={(e) => setRoleNameSearch(e.target.value)}
        />
        <Input
          icon="search"
          placeholder="Search by permission..."
          value={rolePermissionSearch}
          onChange={(e) => setRolePermissionSearch(e.target.value)}
        />
      </FilterContainer>

      {loading ?
        <Loader active inline="centered" />
      : <RoleTable
          roles={filteredRoles}
          permissionGroups={permissionGroups}
          getRoleActions={(role) => (
            <Button
              content="Manage"
              icon="cogs"
              onClick={() => setManagingPermissionsForRoleModalId(role._id)}
            />
          )}
        />
      }
    </div>
  );
};

export default RoleEditor;
